import {
  addDays,
  differenceInCalendarDays,
  format,
  isSameDay,
  parseISO,
} from "date-fns";
import { es } from "date-fns/locale";
import type { Locale } from "date-fns";

export interface RelativeDateLabels {
  /** Word for the current day, e.g. "Hoy" / "Today". */
  today: string;
  /** Word for the next day, e.g. "Mañana" / "Tomorrow". */
  tomorrow: string;
  /** Prefix for a weekday inside the same week, e.g. "Este" / "This". */
  thisPrefix: string;
  /** Suffix appended after the time, e.g. "hs" (empty string to omit). */
  hoursSuffix: string;
}

const DEFAULT_LABELS: RelativeDateLabels = {
  today: "Hoy",
  tomorrow: "Mañana",
  thisPrefix: "Este",
  hoursSuffix: "hs",
};

export interface RelativeDateOptions {
  locale?: Locale;
  labels?: Partial<RelativeDateLabels>;
  /** Override "now" (mainly for tests). */
  now?: Date;
}

/**
 * Human friendly relative date for an upcoming event.
 *
 * Examples (Spanish locale):
 *  - "Hoy, 20:00 hs"
 *  - "Mañana, 20:00 hs"
 *  - "Este viernes, 20:30 hs"
 *  - Falls back to an absolute date (e.g. "lunes 6 jul · 20:00") for dates
 *    more than ~7 days out (or in the past).
 */
export function relativeEventDate(
  input: string | Date,
  options: RelativeDateOptions = {}
): string {
  const date = typeof input === "string" ? parseISO(input) : input;
  if (Number.isNaN(date.getTime())) return "";

  const locale = options.locale ?? es;
  const labels = { ...DEFAULT_LABELS, ...(options.labels ?? {}) };
  const now = options.now ?? new Date();

  const time = format(date, "HH:mm", { locale });
  const suffix = labels.hoursSuffix ? ` ${labels.hoursSuffix}` : "";
  const timePart = `${time}${suffix}`;

  if (isSameDay(date, now)) return `${labels.today}, ${timePart}`;
  if (isSameDay(date, addDays(now, 1))) return `${labels.tomorrow}, ${timePart}`;

  const diffDays = differenceInCalendarDays(date, now);
  if (diffDays > 1 && diffDays <= 7) {
    const weekday = format(date, "EEEE", { locale });
    return `${labels.thisPrefix} ${weekday}, ${timePart}`;
  }

  return format(date, "EEEE d MMM · HH:mm", { locale });
}
